import React from 'react';

/**
 * LoadingSpinner — A non-obstructive loading indicator.
 *
 * Props:
 *  - message      (string)  : Primary text shown next to the spinner.
 *  - subMessage   (string)  : Optional smaller secondary text.
 *  - variant      (string)  : 'banner' (default) | 'inline'
 *                              'banner'  → sticky strip beneath the toolbar, grid stays visible.
 *                              'inline'  → centred block, useful when there is nothing else to show.
 *  - color        (string)  : Tailwind colour key, e.g. 'blue' (default) | 'indigo'.
 */
const LoadingSpinner = ({
  message = 'Loading…',
  subMessage = '',
  variant = 'banner',
  color = 'blue',
}) => {
  /* ---- shared spinner ring ---- */
  const ring = (
    <span className="relative inline-flex shrink-0" style={{ width: 22, height: 22 }}>
      {/* Track */}
      <span
        className="absolute inset-0 rounded-full border-[3px]"
        style={{ borderColor: `var(--spinner-track, #bfdbfe)` }}
      />
      {/* Active arc */}
      <span
        className="absolute inset-0 rounded-full border-[3px] border-t-transparent animate-spin"
        style={{ borderColor: `var(--spinner-arc, #2563eb)`, borderTopColor: 'transparent' }}
      />
    </span>
  );

  /* ---- pulsing dots ---- */
  const dots = (
    <span className="flex gap-1 items-center ml-1">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-70 animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );

  if (variant === 'banner') {
    return (
      <div
        className="w-full px-4 py-2 flex items-center gap-3 text-sm font-medium select-none"
        style={{
          background: 'linear-gradient(90deg, #eff6ff 0%, #eef2ff 100%)',
          borderBottom: '1.5px solid #c7d2fe',
          '--spinner-track': '#c7d2fe',
          '--spinner-arc': '#4f46e5',
          color: '#3730a3',
          zIndex: 20,
        }}
        role="status"
        aria-live="polite"
      >
        {ring}

        <span className="truncate leading-snug">
          {message}
          {subMessage && (
            <span className="ml-1.5 font-normal opacity-70 text-xs">{subMessage}</span>
          )}
        </span>

        {dots}
      </div>
    );
  }

  /* inline / centred fallback */
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-10 px-8 text-center"
      role="status"
      aria-live="polite"
      style={{
        '--spinner-track': '#bfdbfe',
        '--spinner-arc': '#2563eb',
      }}
    >
      {/* Larger spinner for inline mode */}
      <span className="relative inline-flex" style={{ width: 48, height: 48 }}>
        <span
          className="absolute inset-0 rounded-full border-4"
          style={{ borderColor: '#bfdbfe' }}
        />
        <span
          className="absolute inset-0 rounded-full border-4 border-t-transparent animate-spin"
          style={{ borderColor: '#2563eb', borderTopColor: 'transparent' }}
        />
      </span>

      <div>
        <p className="text-base font-semibold text-gray-800">{message}</p>
        {subMessage && (
          <p className="text-sm text-gray-500 mt-0.5">{subMessage}</p>
        )}
      </div>
    </div>
  );
};

export default LoadingSpinner;
